import {
  GenericItem,
  GroupHeader,
  InputItemGroup,
  FlatGroupedItem,
  ItemsProp,
} from '../types';

export const getStartingIndices = <TItem extends GenericItem>(
  groupedItems: InputItemGroup<TItem>[],
) => {
  return groupedItems.reduce((indices, _, index) => {
    if (index === 0) {
      // the first group will always map to the first item
      return indices.set(0, 0);
    }
    return indices.set(
      index,
      groupedItems
        .slice(0, index)
        .reduce((acc, group) => acc + group.items.length, 0),
    );
  }, new Map<number, number>());
};

/**
 * Strips the grouping out of dropdown items, transforming them into items, including a dummy item for the group header.
 * THESE ARE NOT REFERENCE EQUAL TO THE PROVIDED ITEMS.
 * @param groupedItems The grouped dropdown items
 * @param startingIndices The indicies that the group should start at, in the format [groupIndex, firstItemIndex]
 * @returns flattened items without any context of grouping
 */
export const flattenGroupItemsToNonDownshift = <TItem extends GenericItem>(
  groupedItems: InputItemGroup<TItem>[],
  startingIndices: Map<number, number>,
): (GroupHeader | FlatGroupedItem<TItem>)[] =>
  groupedItems.reduce<(GroupHeader | FlatGroupedItem<TItem>)[]>(
    (prev, curr, groupIndex) => {
      const groupKey = `group${groupIndex}`;
      const groupHeader: GroupHeader = {
        label: curr.label,
        groupKey,
        isGroupHeader: true,
      };

      const itemsWithGroupKey = curr.items.map((item, itemIndex) => ({
        ...item,
        index: (startingIndices.get(groupIndex) || 0) + itemIndex,
        groupKey,
      }));

      return [...prev, groupHeader, ...itemsWithGroupKey];
    },
    [],
  );

export const areGroupedItems = <TItem extends GenericItem>(
  maybeGroupedItems: ItemsProp<TItem>,
): maybeGroupedItems is InputItemGroup<TItem>[] => {
  const unsafeMaybeGroupedItems = maybeGroupedItems as InputItemGroup<TItem>[];
  return unsafeMaybeGroupedItems[0] && !!unsafeMaybeGroupedItems[0].items;
};

/**
 * Strips the grouping out of dropdown items, adding group headers and keeping reference equality for downshift
 * @param groupedItems The grouped dropdown items
 * @returns flattened items without any context of grouping
 */
export const flattenGroupItemsToDownshift = <TItem extends GenericItem>(
  maybeGroupedItems: ItemsProp<TItem>,
): TItem[] =>
  areGroupedItems(maybeGroupedItems)
    ? maybeGroupedItems.reduce<TItem[]>((prev, curr) => {
        return [...prev, ...curr.items];
      }, [])
    : maybeGroupedItems;
